import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBillingStore } from '@/stores/billingStore';
import Icon from '../components/Icon';

export const CheckoutCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pollCheckoutStatus } = useBillingStore();

  const paymentId = Number(searchParams.get('paymentId'));
  const initialStatus = searchParams.get('status'); // 'success' or 'error' from SIBS widget

  const [paymentStatus, setPaymentStatus] = useState<string>(initialStatus === 'error' ? 'Failed' : 'Checking');
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!paymentId || initialStatus === 'error') return;

    const poll = async () => {
      const result = await pollCheckoutStatus(paymentId);
      if (result) {
        setPaymentMethod(result.paymentMethod ?? null);
        if (result.paymentStatus === 'Success') {
          setPaymentStatus('Success');
          if (intervalRef.current) clearInterval(intervalRef.current);
        } else if (result.paymentStatus === 'Failed' || result.paymentStatus === 'Declined') {
          setPaymentStatus('Failed');
          if (intervalRef.current) clearInterval(intervalRef.current);
        } else {
          setPaymentStatus('Pending');
          setPollCount((c) => c + 1);
        }
      }
    };

    // Initial check
    poll();

    // Poll every 3 seconds, max 20 times (1 minute)
    intervalRef.current = setInterval(() => {
      setPollCount((c) => {
        if (c >= 20) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setPaymentStatus('Pending');
          return c;
        }
        poll();
        return c;
      });
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paymentId, initialStatus, pollCheckoutStatus]);

  const isSuccess = paymentStatus === 'Success';
  const isFailed = paymentStatus === 'Failed';
  const isPending = paymentStatus === 'Pending';
  const isChecking = paymentStatus === 'Checking';

  return (
    <div className="p-8 max-w-lg mx-auto min-h-[60vh] flex items-center">
      <div className="w-full bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
        {isChecking && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Processing Payment</h2>
            <p className="text-slate-500 dark:text-slate-400">Verifying your payment status...</p>
          </>
        )}

        {isSuccess && (
          <>
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="check_circle" size={36} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Payment Successful</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-1">Your subscription has been activated.</p>
            {paymentMethod && (
              <p className="text-sm text-slate-400 dark:text-slate-500">Paid via {paymentMethod}</p>
            )}
            <button
              onClick={() => navigate('/billing')}
              className="mt-6 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Go to Billing
            </button>
          </>
        )}

        {isFailed && (
          <>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="cancel" size={36} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Payment Failed</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-1">Your payment could not be processed.</p>
            <p className="text-sm text-slate-400 dark:text-slate-500">Please try again or use a different payment method.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button
                onClick={() => navigate('/billing/checkout')}
                className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/billing')}
                className="px-6 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Back to Billing
              </button>
            </div>
          </>
        )}

        {isPending && (
          <>
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="schedule" size={36} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Payment Pending</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-1">
              {paymentMethod === 'REFERENCE'
                ? 'Please complete the Multibanco reference payment. Your subscription will activate once payment is confirmed.'
                : 'Your payment is being processed. This may take a few moments.'}
            </p>
            {pollCount >= 20 && (
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">
                Check your billing page later for updated status.
              </p>
            )}
            <button
              onClick={() => navigate('/billing')}
              className="mt-6 px-6 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              Back to Billing
            </button>
          </>
        )}
      </div>
    </div>
  );
};
